/* Lookbook (GSAP): グリッド(masonry・向きに応じた比率)のサムネイルをクリックすると
   全屏ライトボックスで、画像がクリック元から拡大(FLIP)。
   スライドは横並びのトラックで、prev/next ボタン・キーボード・
   モバイルの左右スワイプ(走馬灯: 指に追従して次の写真が出る)で切替。
   商品情報レイヤーは写真が停止してから表示される。
   GSAP 未読込時は即時表示にフォールバック。 */
class LookbookGallery extends HTMLElement {
  constructor() {
    super();
    this.lightbox = this.querySelector('[data-lookbook-lightbox]');
    this.track = this.querySelector('.lookbook__slides');
    this.slides = Array.from(this.querySelectorAll('[data-lookbook-slide]'));
    this.current = 0;
    this._lastFocus = null;
    this._open = false;
    this._suppressClose = false;
    this._onKeydown = this._onKeydown.bind(this);
  }

  get g() { return window.gsap || null; }
  get reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    if (this._bound || !this.lightbox || !this.track || this.slides.length === 0) return;
    this._bound = true;

    // グリッド画像: 読み込み完了でフェードイン
    this.querySelectorAll('.lookbook__thumb img').forEach((img) => {
      if (!img.complete) {
        img.style.opacity = '0';
        img.addEventListener('load', () => { img.style.opacity = ''; }, { once: true });
        img.addEventListener('error', () => { img.style.opacity = ''; }, { once: true });
      }
    });

    this.querySelectorAll('[data-lookbook-open]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        this.open(parseInt(btn.dataset.lookbookOpen, 10) || 0, btn.querySelector('img'));
      });
    });

    const close = this.querySelector('[data-lookbook-close]');
    const prev = this.querySelector('[data-lookbook-prev]');
    const next = this.querySelector('[data-lookbook-next]');
    if (close) close.addEventListener('click', () => this.close());
    if (prev) prev.addEventListener('click', () => this.navigate(-1));
    if (next) next.addEventListener('click', () => this.navigate(1));

    this.lightbox.addEventListener('click', (e) => {
      if (this._suppressClose) return;
      if (e.target === this.lightbox) this.close();
    });

    if (this.slides.length < 2) {
      if (prev) prev.hidden = true;
      if (next) next.hidden = true;
    }

    this._setupDrag();
    window.addEventListener('resize', () => { if (this._open) this._positionTrack(false); });
  }

  _activeSlide() { return this.slides[this.current]; }
  _activeImg() { const s = this._activeSlide(); return s && s.querySelector('.lookbook__stage-image img'); }
  _activeInfo() { const s = this._activeSlide(); return s && s.querySelector('.lookbook__stage-info'); }
  _allInfos() { return this.slides.map((s) => s.querySelector('.lookbook__stage-info')).filter(Boolean); }
  _slideWidth() { return this.lightbox.clientWidth || window.innerWidth; }

  _setActive(index) {
    const total = this.slides.length;
    this.current = ((index % total) + total) % total;
    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.current));
    const s = this._activeSlide();
    if (s) s.scrollTop = 0;
  }

  // トラックを current スライド位置へ(px 指定)
  _positionTrack(animate, onDone) {
    const x = -this.current * this._slideWidth();
    if (this.g) {
      this.g.killTweensOf(this.track);
      if (animate && !this.reduceMotion) {
        this.g.to(this.track, { x: x, duration: 0.55, ease: 'power3.out', onComplete: onDone });
        return;
      }
      this.g.set(this.track, { x: x });
    } else {
      this.track.style.transform = 'translateX(' + x + 'px)';
    }
    if (onDone) onDone();
  }

  _hideInfos() {
    const infos = this._allInfos();
    if (this.g) this.g.set(infos, { autoAlpha: 0 });
    else infos.forEach((i) => { i.style.opacity = '0'; });
  }

  // 写真が停止してから商品情報を表示
  _revealActiveInfo() {
    const info = this._activeInfo();
    if (!info) return;
    if (this.g && !this.reduceMotion) {
      this.g.killTweensOf(info);
      this.g.fromTo(info, { autoAlpha: 0, y: 14 }, { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power2.out' });
    } else if (this.g) {
      this.g.set(info, { autoAlpha: 1 });
    } else {
      info.style.opacity = '1';
    }
  }

  open(index, thumbImg) {
    if (this._open) { this.goTo(index); return; }
    this._open = true;
    this._suppressClose = true;
    setTimeout(() => { this._suppressClose = false; }, 450);
    this._lastFocus = document.activeElement;

    this._setActive(index);
    this.lightbox.hidden = false;
    document.documentElement.classList.add('lookbook-open');
    document.body.classList.add('lookbook-open');
    document.addEventListener('keydown', this._onKeydown);

    this._hideInfos();
    this._positionTrack(false);

    const g = this.g;
    const img = this._activeImg();

    if (!g || this.reduceMotion) {
      this.lightbox.style.visibility = 'visible';
      this.lightbox.style.opacity = '1';
      this._revealActiveInfo();
    } else {
      g.killTweensOf([this.lightbox, img]);
      g.set(this.lightbox, { opacity: 1, visibility: 'visible' });
      const tl = g.timeline();
      tl.from(this.lightbox, { opacity: 0, duration: 0.25, ease: 'power1.out' }, 0);
      if (img && thumbImg) {
        const first = thumbImg.getBoundingClientRect();
        const last = img.getBoundingClientRect();
        if (first.width && last.width) {
          tl.from(img, {
            x: first.left - last.left,
            y: first.top - last.top,
            scaleX: first.width / last.width,
            scaleY: first.height / last.height,
            transformOrigin: '0 0',
            duration: 0.6,
            ease: 'power3.out'
          }, 0);
        } else {
          tl.from(img, { autoAlpha: 0, duration: 0.4 }, 0);
        }
      }
      // 写真が展開しきってから情報を表示
      tl.add(() => this._revealActiveInfo(), 0.4);
    }

    const closeBtn = this.querySelector('[data-lookbook-close]');
    if (closeBtn) closeBtn.focus();
  }

  goTo(index) {
    const target = ((index % this.slides.length) + this.slides.length) % this.slides.length;
    this._hideInfos();
    this._setActive(target);
    this._positionTrack(true, () => this._revealActiveInfo());
  }

  navigate(dir) {
    if (!dir || this.slides.length < 2) return;
    this.goTo(this.current + dir);
  }

  close() {
    if (!this._open || this._suppressClose) return;
    this._open = false;
    document.documentElement.classList.remove('lookbook-open');
    document.body.classList.remove('lookbook-open');
    document.removeEventListener('keydown', this._onKeydown);

    const finish = () => {
      this.lightbox.hidden = true;
      this.lightbox.style.visibility = 'hidden';
      this.lightbox.style.opacity = '0';
    };
    const g = this.g;
    if (g && !this.reduceMotion) {
      g.killTweensOf(this.lightbox);
      g.to(this.lightbox, { opacity: 0, duration: 0.3, ease: 'power2.in', onComplete: finish });
    } else {
      finish();
    }
    if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
  }

  // モバイル: 指に追従する横スワイプ(走馬灯)。縦は通常スクロール。
  _setupDrag() {
    let startX = 0, startY = 0, axis = null, dragging = false, baseX = 0, lastDx = 0;

    this.track.addEventListener('touchstart', (e) => {
      if (!this._open || e.touches.length !== 1) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      axis = null; dragging = true; lastDx = 0;
      baseX = -this.current * this._slideWidth();
      if (this.g) this.g.killTweensOf(this.track);
    }, { passive: true });

    this.track.addEventListener('touchmove', (e) => {
      if (!dragging) return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (axis === null) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (axis === 'x') {
        e.preventDefault();               // 縦スクロールを止めて横ドラッグ
        lastDx = dx;
        let x = baseX + dx;
        // 端では抵抗をつける
        const min = -(this.slides.length - 1) * this._slideWidth();
        if (x > 0) x = x * 0.35;
        else if (x < min) x = min + (x - min) * 0.35;
        if (this.g) this.g.set(this.track, { x: x });
        else this.track.style.transform = 'translateX(' + x + 'px)';
      }
    }, { passive: false });

    const end = () => {
      if (!dragging) return;
      dragging = false;
      if (axis !== 'x') return;
      const w = this._slideWidth();
      let target = this.current;
      if (lastDx < -w * 0.18) target = this.current + 1;
      else if (lastDx > w * 0.18) target = this.current - 1;
      target = Math.max(0, Math.min(this.slides.length - 1, target));
      if (target === this.current) {
        this._positionTrack(true);        // 戻す
      } else {
        this.goTo(target);
      }
    };
    this.track.addEventListener('touchend', end, { passive: true });
    this.track.addEventListener('touchcancel', end, { passive: true });
  }

  _onKeydown(e) {
    if (e.key === 'Escape') this.close();
    else if (e.key === 'ArrowLeft') this.navigate(-1);
    else if (e.key === 'ArrowRight') this.navigate(1);
  }
}

customElements.define('lookbook-gallery', LookbookGallery);
